<template>
    <div class="container-fluid px-4">

        <!-- Alert when teams scheduling is not enabled -->
        <div v-if="!teamsEnabled" class="alert alert-warning d-flex align-items-center mb-3" role="alert">
            <i class="material-icons me-2">info</i>
            <div>
                <strong>Teams scheduling is not enabled.</strong>
                To use this feature, go to the <strong>Details</strong> tab and set <strong>"Schedule by Teams (in sequential order)"</strong> to <strong>Enabled</strong>.
            </div>
        </div>

        <BaseTable :rows="workgroups" :columns="columns" :loading="loading" searchable :selectable="false">
            <template #buttons>
                <button class="btn btn-info btn-sm mx-2" :disabled="!teamsEnabled" @click="addNewTeam()">
                    Add New Team <i class="material-icons text-lg text-white" color="#e3c9">add</i>
                </button>
                <button class="btn btn-info btn-sm mx-2" :disabled="!teamsEnabled" @click="changeSequence()">
                    Update Team's Sequence <i class="material-icons text-lg text-white" color="#e3c9">swap_vert</i>
                </button>
            </template>
            <template #col-team_members="{ row }">
                <div v-if="row.team_members?.length">
                    <ul v-for="m in row.team_members" :key="m.personid">
                        <li>{{ m.email }}</li>
                    </ul>
                </div>
                <span v-else>-</span>
            </template>
            <template #actions="{ row }">
                <button class="btn btn-sm my-0 py-0" :disabled="!teamsEnabled" @click="editInstances(row)">
                    <i class="material-icons text-lg text-orange">edit</i>
                </button>
                <button class="btn btn-sm my-0 py-0" :disabled="!teamsEnabled" @click="openDeleteModal(row)">
                    <i class="material-icons text-lg text-danger">delete</i>
                </button>
            </template>
        </BaseTable>

        <ContentModal v-model="showLogs" title="Add New Team" size="xl">
            <material-input 
                label="Team Name" 
                id="teamname" 
                name="teamname" 
                class="m-4" 
                v-model="teamName"
                :error="teamNameError"
                required
                @blur="validateTeamName"
                @input="teamNameError = ''" />
            <p class="my-4 form-label">Select Team Members:</p>
            <TagSelector mainHeading="" optionName="team" availableOptionsHeading="Volunteers with skill"
                selectedOptionsHeading="Team Volunteers" :initial-skills="availableAllUsers" :showDefaultMessage="false"
                v-model:interests="currentPreferedUsers" :onSave="handleSaveActivities" :loading="loading"
                :isSaving="submitting" />
        </ContentModal>

        <ContentModal v-model="showEditModal" title="Edit Team" size="xl">
            <material-input 
                label="Team Name" 
                id="edit-teamname" 
                name="edit-teamname" 
                class="m-4" 
                v-model="teamName"
                :error="teamNameError"
                required
                @blur="validateTeamName"
                @input="teamNameError = ''" />
            <p class="my-4 form-label">Select Team Members:</p>
            <TagSelector mainHeading="" optionName="team" availableOptionsHeading="Volunteers with skill"
                selectedOptionsHeading="Team Volunteers" :initial-skills="availableAllUsers" :showDefaultMessage="false"
                v-model:interests="currentPreferedUsers" :onSave="handleSaveActivities" :loading="loading"
                :isSaving="submitting" />
        </ContentModal>

        <DeleteModal 
            v-model:show="showDeleteModal" 
            :itemName="selectedTeamForDelete?.teamname || selectedTeamForDelete?.name || 'this team'" 
            @confirm="deleteTeam" />

        <ContentModal v-model="showTeamSequenceModal" title="Update Team Sequence" size="xl">
            <div class="row gx-4">
                <div class="col-md-6">
                    <label class="form-label">Teams (select one)</label>
                    <select class="form-select sequence-select" size="10" v-model.number="selectedIndex">
                        <option v-for="(t, idx) in sequenceList" :key="t.id" :value="idx">
                            {{ t.teamname ?? t.name ?? ('Team ' + t.id) }}
                        </option>
                    </select>
                </div>

                <div class="col-md-6 d-flex flex-column align-items-stretch">
                    <div class="mb-2">
                        <button class="btn btn-secondary w-100 mb-2" @click="moveUp" :disabled="!canMoveUp">
                            Move Up
                        </button>
                        <button class="btn btn-secondary w-100 mb-2" @click="moveDown" :disabled="!canMoveDown">
                            Move Down
                        </button>
                    </div>

                    <div class="mt-auto">
                        <button class="btn btn-outline-secondary w-100 mb-2" @click="resetOrder">
                            Reset
                        </button>

                        <button class="btn btn-primary w-100" @click="saveSequence"
                            :disabled="savingSequence || !hasChanged">
                            <span v-if="savingSequence" class="spinner-border spinner-border-sm me-2" role="status"
                                aria-hidden="true"></span>
                            {{ savingSequence ? 'Saving...' : 'Save' }}
                        </button>
                    </div>
                </div>
            </div>
        </ContentModal>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toast-notification'
import { useRoute } from 'vue-router'
import BaseTable from '@/components/Table/Table.vue'
import MaterialInput from '@/components/common/MaterialInput.vue'
import ContentModal from '../../../components/Modal/ContentModal.vue'
import TagSelector from '../../../components/common/TagSelector.vue'
import DeleteModal from '@/components/Modal/ConfirmationModal.vue'
import { watch } from 'vue'

defineProps({
    teamsEnabled: { type: Boolean, default: false },
})

const toast = useToast()
const store = useStore()
const route = useRoute()

const showLogs = ref(false)
const showTeamSequenceModal = ref(false)
const showEditModal = ref(false)
const showDeleteModal = ref(false)
const submitting = ref(false);
const editingTeam = ref(null);
const selectedTeamForDelete = ref(null);

const sequenceList = ref([])
const originalSequence = ref([])
const selectedIndex = ref(-1)
const savingSequence = ref(false)
const teamName = ref('')
const teamNameError = ref('')

const workgroupId = Number(route.params.workgroupid)
const locationId = Number(route.params.locationid)
const skillId = route.params.skillid

const workgroups = computed(() => store.getters.workgroupTeams || [])
const loading = computed(() => store.getters.workgroupTeamsLoading)
const preferredPersons = computed(() => store.getters.skillVolunteers);

const availableAllUsers = ref([]);
const currentPreferedUsers = ref([]);

const canMoveUp = computed(() => selectedIndex.value > 0)
const canMoveDown = computed(() => selectedIndex.value >= 0 && selectedIndex.value < sequenceList.value.length - 1)
const hasChanged = computed(() => {
    if (originalSequence.value.length !== sequenceList.value.length) return true
    for (let i = 0; i < sequenceList.value.length; i++) {
        if (sequenceList.value[i].id !== originalSequence.value[i].id) return true
    }
    return false
})

const columns = [
    { key: 'teamname', label: 'Team Name' },
    { key: 'teamseq', label: 'Sequence' },
    { key: 'teamseq', label: 'Count' },
    { key: 'team_members', label: 'Team Members' },
    { key: 'last_schedule_date', label: 'Last Schedule Date' },
]

const fetchInstances = async () => {
    try {
        await store.dispatch("fetchWorkgroupTeams", workgroupId);
    } catch (error) {
        console.error("Failed to fetch workgroup teams:", error);
        toast.error("Failed to fetch workgroup teams", { position: 'top-right' });
    }
}

const editInstances = (team) => {
    editingTeam.value = team;
    teamName.value = team.teamname || team.name || '';
    teamNameError.value = '';
    
    // Load current team members
    if (team.team_members && team.team_members.length > 0) {
        currentPreferedUsers.value = team.team_members.map((m) => ({
            id: m.personid || m.id,
            name: `${m.firstname || m.first_name || ''} ${m.lastname || m.last_name || ''}`.trim(),
            description: m.email || "",
        }));
    } else {
        currentPreferedUsers.value = [];
    }
    
    loadPreferredPersons();
    showEditModal.value = true;
}

const openDeleteModal = (team) => {
    selectedTeamForDelete.value = team;
    showDeleteModal.value = true;
}

const deleteTeam = async () => {
    if (!selectedTeamForDelete.value) return;
    
    submitting.value = true;
    try {
        const teamId = selectedTeamForDelete.value.id || selectedTeamForDelete.value.teamid;
        const teamName = selectedTeamForDelete.value.teamname || selectedTeamForDelete.value.name || '';
        const result = await store.dispatch("deleteWorkgroupTeam", {
            workgroupid: workgroupId,
            locationid: locationId,
            teamid: teamId,
            teamname: teamName,
        });

        if (result && (result.success !== false)) {
            toast.success('Team deleted successfully!', { position: 'top-right' });
            showDeleteModal.value = false;
            selectedTeamForDelete.value = null;
            await fetchInstances();
        } else {
            toast.error(result?.message || 'Failed to delete team', { position: 'top-right' });
        }
    } catch (error) {
        console.error("Failed to delete team:", error);
        toast.error(`Failed to delete team: ${error.message}`, { position: 'top-right' });
    } finally {
        submitting.value = false;
    }
}

const addNewTeam = () => {
    editingTeam.value = null;
    teamName.value = '';
    teamNameError.value = '';
    currentPreferedUsers.value = [];
    loadPreferredPersons();
    showLogs.value = true;
}

const changeSequence = () => {
    showTeamSequenceModal.value = true
}

// ✅ Load volunteers for this skill
const loadPreferredPersons = async () => {
    try {
        await store.dispatch("fetchSkillVolunteers", skillId);

        if (preferredPersons.value) {
            const volunteers = preferredPersons.value.volunteers || preferredPersons.value || [];
            availableAllUsers.value = volunteers.map((v) => ({
                id: v.personid || v.id,
                name: `${v.first_name || v.firstname} ${v.last_name || v.lastname}`,
                description: v.email || "",
            }));

            // Only set currentPreferedUsers if not editing (when editing, it's set in editInstances)
            if (!editingTeam.value) {
                currentPreferedUsers.value = [];
            }
        }
    } catch (error) {
        console.error("Failed to load preferred persons:", error);
        toast.error("Failed to load volunteers", { position: 'top-right' });
    }
};

// ✅ Validate team name
const validateTeamName = () => {
    teamNameError.value = '';
    
    if (!teamName.value || !teamName.value.trim()) {
        teamNameError.value = 'Team name is required';
        return false;
    }
    
    if (teamName.value.trim().length < 2) {
        teamNameError.value = 'Team name must be at least 2 characters';
        return false;
    }
    
    if (teamName.value.trim().length > 100) {
        teamNameError.value = 'Team name must be less than 100 characters';
        return false;
    }
    
    return true;
};

// ✅ Save handler for volunteers
const handleSaveActivities = async (selected) => {
    // Validate team name before saving
    if (!validateTeamName()) {
        toast.error(teamNameError.value, { position: 'top-right' });
        return;
    }
    
    submitting.value = true;
    try {
        const useridsarray = selected.map((x) => x.id);

        if (editingTeam.value) {
            // Edit existing team
            const result = await store.dispatch("editWorkgroupTeam", {
                workgroupid: workgroupId,
                locationid: locationId,
                teamid: editingTeam.value.id || editingTeam.value.teamid,
                teamname: teamName.value,
                useridsarray,
                cmd: "editteam"
            });

            if (result && (result.success !== false)) {
                toast.success('Team updated successfully!', { position: 'top-right' });
                showEditModal.value = false;
                editingTeam.value = null;
                teamName.value = '';
                await fetchInstances();
            } else {
                toast.error(result?.message || 'Failed to update team', { position: 'top-right' });
            }
        } else {
            // Add new team
            const result = await store.dispatch("addWorkgroupTeam", {
                workgroupid: workgroupId,
                locationid: locationId,
                teamname: teamName.value,
                useridsarray,
            });

            if (result && (result.success !== false)) {
                toast.success('Team added successfully!', { position: 'top-right' });
                showLogs.value = false;
                teamName.value = '';
                currentPreferedUsers.value = [];
                await fetchInstances();
            } else {
                toast.error(result?.message || 'Failed to add team', { position: 'top-right' });
            }
        }
    } catch (error) {
        console.error("Failed to save team:", error);
        toast.error(`Failed to save team: ${error.message}`, { position: 'top-right' });
    } finally {
        submitting.value = false;
    }
};

onMounted(() => {
    // loading.value = true; // Managed by store getter
    fetchInstances()
})

watch(showTeamSequenceModal, (open) => {
    if (open) {
        // build a shallow copy of current workgroups into sequenceList
        sequenceList.value = (workgroups.value || []).map(w => ({
            id: w.id ?? w.teamid ?? w.workgroupid,
            teamname: w.teamname ?? w.name ?? w.team_name,
            // keep original object available if you need it:
            __source: w
        }))
        // snapshot original order to allow Reset
        originalSequence.value = sequenceList.value.map(s => ({ ...s }))
        // select first item by default (if exists)
        selectedIndex.value = sequenceList.value.length ? 0 : -1
    } else {
        // when closing, reset selection for next open
        selectedIndex.value = -1
    }
})

// move functions
const moveUp = () => {
    if (!canMoveUp.value) return
    const i = selectedIndex.value
    const arr = sequenceList.value
    const tmp = arr[i - 1]
    arr.splice(i - 1, 2, arr[i], tmp) // swap
    selectedIndex.value = i - 1
}

const moveDown = () => {
    if (!canMoveDown.value) return
    const i = selectedIndex.value
    const arr = sequenceList.value
    const tmp = arr[i + 1]
    arr.splice(i, 2, tmp, arr[i]) // swap
    selectedIndex.value = i + 1
}

const resetOrder = () => {
    sequenceList.value = originalSequence.value.map(s => ({ ...s }))
    selectedIndex.value = sequenceList.value.length ? 0 : -1
}

// Save to store (adapt action name if different in your store)
const saveSequence = async () => {
    if (savingSequence.value) return
    savingSequence.value = true
    try {
        // Build teamidsarray from sequenceList in order
        const teamidsarray = sequenceList.value.map((t) => t.id);
        const res = await store.dispatch('saveTeamSequence', {
            workgroupid: workgroupId,
            locationid: locationId,
            teamidsarray
        })

        // Handle response
        if (res && (res.success !== false)) {
            toast.success('Team sequence saved successfully.', { position: 'top-right' })
            // refresh list from server
            // await fetchInstances(currentPage.value, pagination.value.pageSize)
            await fetchInstances()

            showTeamSequenceModal.value = false
        } else {
            // show error if available
            const msg = (res && res.message) ? res.message : 'Failed to save sequence'
            toast.error(msg, { position: 'top-right' })
        }
    } catch (err) {
        console.error('Error saving sequence:', err)
        toast.error(err.message || 'Error saving sequence', { position: 'top-right' })
    } finally {
        savingSequence.value = false
    }
}

</script>

<style>
.sequence-select {
    width: 100%;
    min-height: 220px;
    max-height: 420px;
    overflow: auto;
    font-size: 0.95rem;
}
</style>



<!-- //pagination
<template>
    <div class="container-fluid px-4">

        <BaseTable :rows="workgroups" :columns="columns" :loading="loading" searchable :selectable="false"
            :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
            @searchChanged="onSearchChanged">
            <template #buttons>
                <button class="btn btn-info btn-sm mx-2" @click="addNewTeam()">
                    Add New Team <i class="material-icons text-lg text-white" color="#e3c9">add</i>
                </button>
                <button class="btn btn-info btn-sm mx-2" @click="changeSequence()">
                    Update Team's Sequence <i class="material-icons text-lg text-white" color="#e3c9">swap_vert</i>
                </button>
            </template>
            <template #actions="{ row }">
                <button class="btn btn-sm my-0 py-0" @click="editInstances(row.id)">
                    <i class="material-icons text-lg text-orange">edit</i>
                </button>
                <button class="btn btn-sm my-0 py-0" @click="openDeleteModal(row)">
                    <i class="material-icons text-lg text-info">delete</i>
                </button>
            </template>
        </BaseTable>

        <ContentModal v-model="showLogs" title="Add New Team" size="xl">
            <TagSelector mainHeading="" optionName="team" availableOptionsHeading="Volunteers with skill"
                selectedOptionsHeading="Team Volunteers" :initial-skills="availableAllUsers" :showDefaultMessage="false"
                v-model:interests="currentPreferedUsers" :onSave="handleSaveActivities" :loading="loading"
                :isSaving="submitting" />
        </ContentModal>

        <DeleteModal 
            v-model:show="showDeleteModal" 
            :itemName="selectedTeamForDelete?.teamname || selectedTeamForDelete?.name || 'this team'" 
            @confirm="deleteTeam" />

        <ContentModal v-model="showTeamSequenceModal" title="Update Team Sequence" size="xl">
            <div class="row gx-4">
                <div class="col-md-6">
                    <label class="form-label">Teams (select one)</label>
                    <select class="form-select sequence-select" size="10" v-model.number="selectedIndex">
                        <option v-for="(t, idx) in sequenceList" :key="t.id" :value="idx">
                            {{ t.teamname ?? t.name ?? ('Team ' + t.id) }}
                        </option>
                    </select>
                </div>

                <div class="col-md-6 d-flex flex-column align-items-stretch">
                    <div class="mb-2">
                        <button class="btn btn-secondary w-100 mb-2" @click="moveUp" :disabled="!canMoveUp">
                            Move Up
                        </button>
                        <button class="btn btn-secondary w-100 mb-2" @click="moveDown" :disabled="!canMoveDown">
                            Move Down
                        </button>
                    </div>

                    <div class="mt-auto">
                        <button class="btn btn-outline-secondary w-100 mb-2" @click="resetOrder">
                            Reset
                        </button>

                        <button class="btn btn-primary w-100" @click="saveSequence"
                            :disabled="savingSequence || !hasChanged">
                            <span v-if="savingSequence" class="spinner-border spinner-border-sm me-2" role="status"
                                aria-hidden="true"></span>
                            {{ savingSequence ? 'Saving...' : 'Save' }}
                        </button>
                    </div>
                </div>
            </div>
        </ContentModal>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toast-notification'
import { useRoute, useRouter } from 'vue-router'
import BaseTable from '@/components/Table/Table.vue'
import ContentModal from '../../../components/Modal/ContentModal.vue'
import TagSelector from '../../../components/common/TagSelector.vue'
import { watch } from 'vue'

const toast = useToast()
const store = useStore()
const route = useRoute()
const router = useRouter()

const searchTerm = ref('')
const showLogs = ref(false)
const showTeamSequenceModal = ref(false)
const submitting = ref(false);

const sequenceList = ref([])
const originalSequence = ref([])
const selectedIndex = ref(-1)
const savingSequence = ref(false)

const workgroupId = Number(route.params.workgroupid)
const locationId = Number(route.params.locationid)

const workgroups = computed(() => store.getters.workgroupTeams || [])
const pagination = computed(() => store.getters.workgroupInstancesPagination)
const loading = computed(() => store.getters.workgroupInstancesLoading)
const preferredPersons = computed(() => store.getters.preferredPersons);

const availableAllUsers = ref([]);
const currentPreferedUsers = ref([]);

const canMoveUp = computed(() => selectedIndex.value > 0)
const canMoveDown = computed(() => selectedIndex.value >= 0 && selectedIndex.value < sequenceList.value.length - 1)
const hasChanged = computed(() => {
    if (originalSequence.value.length !== sequenceList.value.length) return true
    for (let i = 0; i < sequenceList.value.length; i++) {
        if (sequenceList.value[i].id !== originalSequence.value[i].id) return true
    }
    return false
})

const columns = [
    { key: 'teamname', label: 'Team Name' },
    { key: 'sequence', label: 'Sequence' },
    { key: 'count', label: 'Count' },
    { key: 'teammembers', label: 'Team Members' },
    { key: 'lastscheduledate', label: 'Last Schedule Date' },
]

const fetchInstances = async () => {
    loading.value = true;
    await store.dispatch("ScheduleByTeam/fetchWorkgroupTeams", workgroupId);
    loading.value = false;
}

const onChangePage = (page) => {
    if (!page) return
    fetchInstances(page, pagination.value.pageSize) // Pass current pageSize
}

const onUpdatePageSize = (size) => {
    fetchInstances(1, size)
}

const onSearchChanged = (term) => {
    searchTerm.value = term
    fetchInstances(1, pagination.value.pageSize) // Pass current pageSize
}

const editInstances = (id) => {
    router.push({
        name: 'editInstances',
        params: { locationId, workgroupid: id },
    })
}

const openDeleteModal = (id) => {
    console.log(id);

}

const addNewTeam = () => {
    showLogs.value = true;
}

const changeSequence = () => {
    showTeamSequenceModal.value = true
}

// ✅ Load volunteers for this skill
const loadPreferredPersons = async () => {
    try {
        await store.dispatch("fetchPreferredPersons", workgroupId);

        availableAllUsers.value = preferredPersons.value.skilled_persons.map((v) => ({
            id: v.personid,
            name: `${v.firstname} ${v.lastname}`,
            description: v.email || "",
        }));

        currentPreferedUsers.value = preferredPersons.value.preferred_persons.map((v) => ({
            id: v.personid,
            name: `${v.firstname} ${v.lastname}`,
            description: v.email || "",
        }));
    } catch (error) {
        console.error("Failed to load preferred persons:", error);
    }
};

// ✅ Save handler for volunteers
const handleSaveActivities = async (selected) => {
    submitting.value = true;
    try {
        const personIds = selected.map((x) => x.id);

        const result = await store.dispatch("savePreferredPersons", {
            workgroupId,
            personIds,
        });

        if (result.success) {
            toast.success('Preferred persons saved successfully!', { position: 'top-right' });
            await loadPreferredPersons();
        }
    } catch (error) {
        console.error("Failed to save preferred persons:", error);
        toast.error(`Failed to save preferred persons: ${error.message}`, { position: 'top-right' });
    } finally {
        submitting.value = false;
    }
};

onMounted(() => {
    // loading.value = true; // Managed by store getter
    fetchInstances()
})

watch(showTeamSequenceModal, (open) => {
    if (open) {
        // build a shallow copy of current workgroups into sequenceList
        sequenceList.value = (workgroups.value || []).map(w => ({
            id: w.id ?? w.teamid ?? w.workgroupid,
            teamname: w.teamname ?? w.name ?? w.team_name,
            // keep original object available if you need it:
            __source: w
        }))
        // snapshot original order to allow Reset
        originalSequence.value = sequenceList.value.map(s => ({ ...s }))
        // select first item by default (if exists)
        selectedIndex.value = sequenceList.value.length ? 0 : -1
    } else {
        // when closing, reset selection for next open
        selectedIndex.value = -1
    }
})

// move functions
const moveUp = () => {
    if (!canMoveUp.value) return
    const i = selectedIndex.value
    const arr = sequenceList.value
    const tmp = arr[i - 1]
    arr.splice(i - 1, 2, arr[i], tmp) // swap
    selectedIndex.value = i - 1
}

const moveDown = () => {
    if (!canMoveDown.value) return
    const i = selectedIndex.value
    const arr = sequenceList.value
    const tmp = arr[i + 1]
    arr.splice(i, 2, tmp, arr[i]) // swap
    selectedIndex.value = i + 1
}

const resetOrder = () => {
    sequenceList.value = originalSequence.value.map(s => ({ ...s }))
    selectedIndex.value = sequenceList.value.length ? 0 : -1
}

// Save to store (adapt action name if different in your store)
const saveSequence = async () => {
    if (savingSequence.value) return
    savingSequence.value = true
    try {
        // Build teamidsarray from sequenceList in order
        const teamidsarray = sequenceList.value.map((t) => t.id);
        const res = await store.dispatch('saveTeamSequence', {
            workgroupid: workgroupId,
            locationid: locationId,
            teamidsarray
        })

        // Handle response
        if (res && (res.success !== false)) {
            toast.success('Team sequence saved successfully.', { position: 'top-right' })
            // refresh list from server
            // await fetchInstances(currentPage.value, pagination.value.pageSize)
            await fetchInstances()

            showTeamSequenceModal.value = false
        } else {
            // show error if available
            const msg = (res && res.message) ? res.message : 'Failed to save sequence'
            toast.error(msg, { position: 'top-right' })
        }
    } catch (err) {
        console.error('Error saving sequence:', err)
        toast.error(err.message || 'Error saving sequence', { position: 'top-right' })
    } finally {
        savingSequence.value = false
    }
}

</script>

<style>
.sequence-select {
    width: 100%;
    min-height: 220px;
    max-height: 420px;
    overflow: auto;
    font-size: 0.95rem;
}
</style> -->